<script setup lang="ts">
import { nextTick, ref, useTemplateRef, watch } from "vue";

import ActionSource from "@/components/action/ActionSource.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import { makeBoardRegionsFocusable, makeBoardRegionsUnfocusable } from "@/directives/boardRegion";
import { initViewportCoord } from "@/model/coordinates";
import type { ContextMenuProps, EventHandlers, VueComponent } from "@/store/contextMenu";
import { useContextMenuStore } from "@/store/contextMenu";
import { useZoomStore } from "@/store/zoom";

const contextMenuStore = useContextMenuStore();
const zoomStore = useZoomStore();

const dropdownMenuRef = useTemplateRef("dropdownMenu");
const triggerRef = useTemplateRef("trigger");
const dropdownRef = useTemplateRef("dropdown");

const component = ref<VueComponent<unknown> | null>(null);
const menuProps = ref<ContextMenuProps>({ position: initViewportCoord() });
const handlers = ref<EventHandlers>({});

const handleHideContextMenu = () => {
  contextMenuStore.close();
};

watch(
  () => zoomStore.zooming,
  (val) => {
    if (val && contextMenuStore.menu) {
      handleHideContextMenu();
      dropdownMenuRef.value?.close();
    }
  },
);

watch(
  () => contextMenuStore.menu,
  async (menu) => {
    if (menu) {
      // opening the context menu may trigger focus on a board region
      // which may be autoscrolled into view. Avoid this.
      makeBoardRegionsUnfocusable();
      component.value = menu.component;
      menuProps.value = menu.props;
      handlers.value = menu.eventHandlers || {};

      await nextTick();

      if (!dropdownRef.value) {
        triggerRef.value?.click();
      }
    }
    if (!menu) {
      void handlers.value.close?.();
      makeBoardRegionsFocusable();
    }
  },
);
</script>

<template>
  <DropdownMenu ref="dropdownMenu" no-flip @close="handleHideContextMenu">
    <template #trigger>
      <div
        ref="trigger"
        style="position: fixed"
        :style="{ left: menuProps.position.x + 'px', top: menuProps.position.y + 'px' }"
      />
    </template>
    <!-- @vue-ignore -->
    <ActionSource value="context-menu">
      <component :is="component" ref="dropdown" v-bind="menuProps" v-on="handlers" />
    </ActionSource>
  </DropdownMenu>
</template>
